import { DatabaseAdd, ThreeDotsVertical } from "react-bootstrap-icons"
import TableHeader from "../Components/TableHeader"
import { useCallback, useEffect, useMemo, useState } from "react"
import TableLoader from "../Components/TableLoader"
import OutsideClickHandler from "react-outside-click-handler"
import TableFoot from "../Components/TableFoot"
import DialogModal from "../Components/DialogModal"
import { Modal } from "react-bootstrap"
import api from "../lib/api"
import useNotify from "../hooks/useNotify"
import useListData from "../hooks/useListData"
import useTableFilter from "../hooks/useTableFilter"
import useSortOptions from "../hooks/useSortOptions"

const WatchList = ({ setNotification }) => {
    const notify = useNotify(setNotification);
    const { filter, setFilter, clampPage } = useTableFilter({
        page: 1, limit: 10, keyword: "", sort_by: "id", sort_order: "DESC", auction: "",
    });
    const [active, setActive] = useState("");
    const [dialog, setDialog] = useState({ title: "", text: "", show: false, entry: null, loader: false });
    const [viewFullScreenImage, setViewFullScreenImage] = useState({ show: false, src: "" });

    const sortBy = useSortOptions("accounts-sort-by", notify);
    const onListError = useCallback((err) => notify.error(err), [notify]);
    const { data: watched, total, loading, reload } = useListData("/watch", filter, { onError: onListError });

    const pageCount = useMemo(() => Math.max(1, Math.ceil(total / filter.limit)), [total, filter.limit]);
    useEffect(() => { clampPage(total); }, [total, clampPage]);

    const handlers = {
        openRemoveModal(entry) {
            setDialog({
                show: true,
                title: "Remove From Watchlist",
                text: `Remove lot ${entry.lot} from your watchlist?`,
                entry,
                loader: false,
            });
            setActive("");
        },
        removeFromWatchlist() {
            const entry = dialog.entry;
            if (!entry) return;

            setDialog((prev) => ({ ...prev, loader: true }));

            // POST /watch toggles the entry off. The previous handler called
            // DELETE /accounts/:id with the watch row's id, which deleted an
            // unrelated buyer account that happened to share that id and left
            // the watchlist entry in place.
            api.post('/watch', { lot: entry.lot, buyer: entry.buyer, auction: entry.auction })
                .then(() => {
                    notify.success("Removed from watchlist");
                    reload();
                })
                .catch((err) => notify.error(err))
                .finally(() => setDialog({ show: false, title: "", text: "", entry: null, loader: false }));
        },
    };

    return (
        <div className="page-content-container">
            <h2 className="page-title">Watch List</h2>
            <TableHeader
                withoutAddNew={true}
                setNotification={setNotification}
                AddNewButtonIcon={DatabaseAdd}
                filter={filter}
                sortBy={sortBy}
                exportButton={false}
                checkboxes={[]}
                setFilter={setFilter}
            />
            <div className="table-scroll">
                <table className="responsive-d-table">
                    <thead>
                        <tr>
                            <th>Image</th>
                            <th>Name</th>
                            <th>Lot</th>
                            <th>Stock</th>
                            <th>Odometer</th>
                            <th>Damage</th>
                            <th>Has Key</th>
                            <th>Location</th>
                            <th>Auction</th>
                            <th><span className="sr-only">Actions</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {(!loading && watched.length > 0) && watched.map((entry, id) => (
                            <tr key={entry.id} className={(id + 2) % 2 === 0 ? "even-row" : "odd-row"}>
                                <td>
                                    {entry.image
                                        ? <img
                                            onClick={() => setViewFullScreenImage({ show: true, src: entry.image })}
                                            src={entry.image}
                                            alt={entry.name || "vehicle"}
                                            className="watch-thumb"
                                            loading="lazy"
                                        />
                                        : <span className="watch-thumb watch-thumb-empty" aria-hidden="true" />}
                                </td>
                                <td>{entry.name}</td>
                                <td>
                                    {/* rel added: target="_blank" without it hands the
                                        opened page a reference back to this window. */}
                                    <a
                                        href={entry.auction === "copart"
                                            ? `https://copart.com/lot/${entry.lot}`
                                            : `https://www.iaai.com/VehicleDetail/${entry.lot}~US`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >{entry.lot}</a>
                                </td>
                                <td>{entry.stock}</td>
                                <td>{entry.odometer}</td>
                                <td>{entry.damage}</td>
                                <td>{entry.has_key}</td>
                                <td>{entry.location}</td>
                                <td>{entry.auction}</td>
                                <td>
                                    <button
                                        type="button"
                                        className="row-actions-trigger"
                                        aria-label={`Actions for lot ${entry.lot}`}
                                        onClick={() => setActive((prev) => (prev === entry.id ? "" : entry.id))}
                                    >
                                        <ThreeDotsVertical />
                                    </button>
                                    {active === entry.id && <OutsideClickHandler onOutsideClick={() => setActive("")}>
                                        <div className="table-actions-box">
                                            <ul>
                                                <li onClick={() => handlers.openRemoveModal(entry)}>Remove</li>
                                            </ul>
                                        </div>
                                    </OutsideClickHandler>}
                                </td>
                            </tr>
                        ))}
                        {(!loading && watched.length === 0) && <tr className="empty-row">
                            <td colSpan={10}><div className="empty-records"><span>Records not found</span></div></td>
                        </tr>}
                        {loading && <TableLoader colspan={10} />}
                    </tbody>
                </table>
            </div>
            <TableFoot filter={filter} setFilter={setFilter} count={pageCount} total={total} />
            <DialogModal
                show={dialog.show}
                setShow={(value) => setDialog((prev) => ({ ...prev, show: value }))}
                title={dialog.title}
                text={dialog.text}
                onSubmit={handlers.removeFromWatchlist}
                loader={dialog.loader}
            />
            <Modal size="lg" centered show={viewFullScreenImage.show} onHide={() => setViewFullScreenImage({ show: false, src: "" })}>
                <Modal.Body style={{ padding: 0, background: "var(--surface-2)" }}>
                    <img src={viewFullScreenImage.src} alt="Vehicle, full size" style={{ width: "100%", display: "block" }} />
                </Modal.Body>
            </Modal>
        </div>
    )
}

export default WatchList
